import { CANVAS_PATH, navigateToCanvas } from "./canvas-navigation";
import { track } from "./analytics";
import { markCanvasEntry } from "./canvas-entry";
import type { SearchProjectTheme } from "./search-project-themes";

export { CANVAS_PATH } from "./canvas-navigation";

/**
 * Always the landing page, whatever this browser has stored. The canonical tag
 * stays on `/`, so this second path doesn't split the SEO.
 */
export const LANDING_PATH = "/landing";

export type LandingGateInput = {
  pathname: string;
  search: string;
};

function normalizePath(pathname: string): string {
  const p = pathname.toLowerCase();
  if (p.length > 1 && p.endsWith("/")) return p.slice(0, -1);
  return p || "/";
}

export function shouldShowLanding(input: LandingGateInput): boolean {
  const path = normalizePath(input.pathname);
  if (path === LANDING_PATH) return true;

  if (path !== "/") return false;

  // These name a specific schematic: a destination, not an arrival to pitch to.
  const params = new URLSearchParams(input.search);
  if (params.get("p") || params.get("s") || params.get("pub")) return false;

  return true;
}

/**
 * Legacy project/share links used the root before the canvas had its own path.
 * Move only those explicit destinations to `/app`, preserving their query and
 * hash. A bare `/` is always the homepage, regardless of browser or auth state.
 */
export function canvasUrlRewrite(
  input: LandingGateInput & { hash: string },
): string | null {
  if (shouldShowLanding(input)) return null;
  if (normalizePath(input.pathname) !== "/") return null;
  return `${CANVAS_PATH}${input.search}${input.hash}`;
}

/**
 * A full navigation, not a client-side swap: the SPA boots with a clean store,
 * so the hero's demo schematic can't be autosaved as the visitor's first project.
 */
export function openCanvas(source: string, props?: CanvasCtaProps): void {
  trackCanvasCta(source, props);
  navigateToCanvas(`${CANVAS_PATH}?new=1`);
}

export type CanvasCtaProps = {
  /** The catalog id a SKU page's CTA carries into the editor, so the click
   * can be read per part and not only per page kind. */
  component?: string;
  searchProjectCta?: {
    experiment: "search-project-cta-v1";
    variant: "control" | "test";
    theme: SearchProjectTheme;
  };
};

/**
 * Two records of one click. The event goes out instantly because the page is
 * about to unload; the handoff lets the editor report the same hop on boot as
 * `app_entered`, which cannot be lost to the navigation.
 */
export function trackCanvasCta(source: string, props?: CanvasCtaProps, options?: { markEntry?: boolean }): void {
  // A native new-tab click must not leave attribution for a later, unrelated
  // editor visit in the original tab. Its actual URL still identifies the demo.
  if (options?.markEntry !== false) markCanvasEntry(source);
  track(
    "landing_cta_clicked",
    {
      cta: "open_canvas",
      source,
      ...(props?.component ? { component: props.component } : {}),
      ...(props?.searchProjectCta ? {
        cta_experiment: props.searchProjectCta.experiment,
        cta_variant: props.searchProjectCta.variant,
        search_project_theme: props.searchProjectCta.theme,
      } : {}),
    },
    { instant: true },
  );
}
